import { useState, useCallback } from 'react';
import { ShoppingItem, StockBatch, IngredientDetail, UserInventory } from '../types';

interface UseShoppingProps {
  initialShoppingList: ShoppingItem[];
  onShoppingListChange: (shoppingList: ShoppingItem[]) => void;
}

interface UseShoppingReturn {
  // State
  shoppingList: ShoppingItem[];
  setShoppingList: (shoppingList: ShoppingItem[] | ((prev: ShoppingItem[]) => ShoppingItem[])) => void;

  // Handlers
  handleAddToShoppingList: (
    ingredientName: string,
    selectedDate: string,
    showToastMessage: (msg: string) => void,
    options?: {
      linkedIngredientId?: string;
      quantity?: string;
      date?: string;
    }
  ) => void;
  handleCheckout: (items: ShoppingItem[], ingredients: IngredientDetail[], inventory: UserInventory, setInventory: (inventory: UserInventory) => void, showToastMessage: (msg: string) => void, awardXP: (amount: number) => void) => void;
}

export const useShopping = ({ initialShoppingList, onShoppingListChange }: UseShoppingProps): UseShoppingReturn => {
  const [shoppingList, setShoppingListInternal] = useState<ShoppingItem[]>(initialShoppingList);

  // Sync with external shopping list when it changes
  const setShoppingList = useCallback((newShoppingList: ShoppingItem[] | ((prev: ShoppingItem[]) => ShoppingItem[])) => {
    setShoppingListInternal(prev => {
      const updated = typeof newShoppingList === 'function' ? newShoppingList(prev) : newShoppingList;
      onShoppingListChange(updated);
      return updated;
    });
  }, [onShoppingListChange]);

  // Handle add to shopping list
  const handleAddToShoppingList = useCallback((
    ingredientName: string,
    selectedDate: string,
    showToastMessage: (msg: string) => void,
    options?: {
      linkedIngredientId?: string;
      quantity?: string;
      date?: string;
    }
  ) => {
    const newItem: ShoppingItem = {
      id: `s-${Date.now()}`,
      name: ingredientName,
      quantity: options?.quantity || '100g',
      checked: false,
      date: options?.date || selectedDate,
      linkedIngredientId: options?.linkedIngredientId,
    };

    setShoppingList(prev => [...prev, newItem]);
    showToastMessage(`已添加 "${ingredientName}" 到购物清单`);
  }, [setShoppingList]);

  // Handle checkout from shopping
  const handleCheckout = useCallback((items: ShoppingItem[], ingredients: IngredientDetail[], inventory: UserInventory, setInventory: (inventory: UserInventory) => void, showToastMessage: (msg: string) => void, awardXP: (amount: number) => void) => {
    items.forEach(item => {
      if (item.linkedIngredientId && item.batchData) {
        setInventory({
          ...inventory,
          [item.linkedIngredientId]: (() => {
            const existingInventory = inventory[item.linkedIngredientId];
            if (!existingInventory) {
              const ingredient = ingredients.find(ing => ing.id === item.linkedIngredientId);
              const newInventoryItem = {
                currentAmount: 0,
                requiredAmount: 0,
                unit: ingredient?.unit || 'g',
                batches: []
              };
              
              const newBatch: StockBatch = {
                id: `batch-${Date.now()}-${item.linkedIngredientId}`,
                date: new Date().toISOString().split('T')[0],
                expirationDate: item.batchData?.expirationDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                quantity: item.batchData?.quantity || parseFloat(item.quantity) || 1,
                unit: item.batchData?.unit || newInventoryItem.unit,
                location: item.batchData?.location || '冰箱冷藏',
                daysLeft: 7,
                note: item.batchData?.note
              };

              return {
                ...newInventoryItem,
                batches: [newBatch],
                currentAmount: newBatch.quantity
              };
            }

            const newBatch: StockBatch = {
              id: `batch-${Date.now()}-${item.linkedIngredientId}`,
              date: new Date().toISOString().split('T')[0],
              expirationDate: item.batchData?.expirationDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              quantity: item.batchData?.quantity || parseFloat(item.quantity) || 1,
              unit: item.batchData?.unit || existingInventory.unit,
              location: item.batchData?.location || '冰箱冷藏',
              daysLeft: 7,
              note: item.batchData?.note
            };

            return {
              ...existingInventory,
              batches: [...existingInventory.batches, newBatch],
              currentAmount: existingInventory.currentAmount + newBatch.quantity
            };
          })()
        });
      }
    });
    showToastMessage(`已入库 ${items.length} 项`);
    awardXP(items.length * 3);
  }, []);

  return {
    shoppingList,
    setShoppingList,
    handleAddToShoppingList,
    handleCheckout,
  };
};
